import { storeToRefs } from 'pinia'
import {
  computed,
  onScopeDispose,
  ref,
  unref,
  watch,
  type ComputedRef,
  type MaybeRef,
  type Ref,
} from 'vue'

import { alertApi } from '../api/AlertApi'
import type { AlertRule } from '../models/alert.types'
import type { ChannelAvailability } from '../models/channel.types'
import type { AlertHistoryRow } from '../models/history.types'
import { useAlertsStore, type AlertsLoadStatus } from '../stores/alertsStore'

import { useWorkspace } from '@/features/account'
import { useNotificationChannels } from '@/features/workspace-settings'
import { ALERT_NOTIFICATION_TYPE_NAME } from '@/shared/config'
import type { ApiError } from '@/shared/http'

export type AlertWorkspaceScope = number | string | { id: number | string }

export interface UseAlertsOptions {
  workspaceId?: MaybeRef<number | string | undefined>
  accountWorkspaces?: MaybeRef<readonly AlertWorkspaceScope[] | undefined>
  autoLoad?: boolean
}

export interface UseAlerts {
  status: ComputedRef<AlertsLoadStatus>
  error: ComputedRef<ApiError | null>
  rules: Ref<AlertRule[]>
  history: Ref<AlertHistoryRow[]>
  channels: Ref<ChannelAvailability[]>
  togglingId: Ref<number | null>
  workspaceIds: ComputedRef<Array<number | string>>
  notificationTypeId: Ref<number | null>
  filteredRules: ComputedRef<AlertRule[]>
  filteredHistory: ComputedRef<AlertHistoryRow[]>
  query: Ref<string>
  reload: () => Promise<void>
  toggleActive: (rule: AlertRule) => Promise<void>
  remove: (rule: AlertRule) => Promise<void>
}

function scopeId(value: AlertWorkspaceScope): number | string {
  return typeof value === 'object' ? value.id : value
}

export function useAlerts(options: UseAlertsOptions = {}): UseAlerts {
  const store = useAlertsStore()
  const selectedWorkspace = useWorkspace()
  const notificationChannels = useNotificationChannels()
  const { rules, history, channels, togglingId } = storeToRefs(store)
  const query = ref('')
  const notificationTypeId = ref<number | null>(null)
  const resolving = ref(false)
  const preflightError = ref<ApiError | null>(null)

  const workspaceIds = computed(() => {
    const supplied = unref(options.accountWorkspaces)
    if (supplied?.length) return supplied.map(scopeId)
    const explicit = unref(options.workspaceId)
    const fallback = selectedWorkspace.workspaceId.value
    return explicit === undefined ? (fallback === undefined ? [] : [fallback]) : [explicit]
  })
  const scopeKey = computed(() => workspaceIds.value.map(String).join('|'))
  const status = computed<AlertsLoadStatus>(() =>
    resolving.value ? 'loading' : preflightError.value ? 'error' : store.status,
  )
  const error = computed(() => preflightError.value ?? store.error)
  const filteredRules = computed(() => store.rulesByName(query.value))
  const filteredHistory = computed(() => store.historyByName(query.value))

  async function reload(): Promise<void> {
    const ids = workspaceIds.value
    preflightError.value = null
    if (!ids.length) {
      notificationTypeId.value = null
      store.reset()
      return
    }
    resolving.value = true
    try {
      const resolvedNotificationTypeId = await alertApi.resolveNotificationTypeId(
        ALERT_NOTIFICATION_TYPE_NAME,
      )
      notificationTypeId.value = resolvedNotificationTypeId
      await store.load(ids, {
        notificationTypeId: resolvedNotificationTypeId,
        // Every workspace in scope gets its OWN channel read, as v3 did
        // (`views/App/Tools/Alerts.vue:537`, inside `wsIds.map`). This used to read 22's
        // selected-workspace state and return `[]` for anything else, so an account-scoped list
        // rendered its other workspaces as having no channels at all. `alertService` already folds a
        // per-workspace rejection into an `AlertUnavailableSlice`, so a failure is reported rather
        // than silently empty.
        loadChannels: (workspaceId) => notificationChannels.loadFor(workspaceId),
      })
    } catch (cause) {
      notificationTypeId.value = null
      preflightError.value = cause as ApiError
    } finally {
      resolving.value = false
    }
  }

  if (options.autoLoad !== false) {
    watch(scopeKey, () => void reload(), { immediate: true })
  }
  onScopeDispose(() => store.reset())

  return {
    status,
    error,
    rules,
    history,
    channels,
    togglingId,
    workspaceIds,
    notificationTypeId,
    filteredRules,
    filteredHistory,
    query,
    reload,
    toggleActive: store.toggleActive,
    remove: store.remove,
  }
}
